import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Image } from "../../../Utility/MediaBuilder/Image";
import Modal, { IModalProps } from "../../../Utility/Modal/Modal";
import { IProductGalleryProps } from "../ProductGallery";
import { Carousel, ICarousel } from "../../../ActionsSelectors/Carousel";

export interface IFullscreenImageProps
  extends Omit<IProductGalleryProps, "classes">,
    Omit<IModalProps, "classes"> {
  classes?: {
    root?: IProductGalleryProps["classes"];
    carousel?: ICarousel["classes"];
  };
}

const FullscreenImage: FC<IFullscreenImageProps> = ({
  classes,
  images = [],
  activeItem,
  onSelect,
  ...modal
}) => {
  const [primaryInstance, setPrimaryInstance] =
    useState<ICarousel["params"]["mainCarouselInstance"]>(null);

  const activeIndex = useMemo(
    () =>
      activeItem ? images.findIndex((item) => item.id === activeItem.id) : 0,
    [activeItem, images],
  );

  useEffect(() => {
    if (!primaryInstance || primaryInstance?.destroyed) return;
    primaryInstance.slideTo(activeIndex);
  }, [activeIndex, primaryInstance]);

  const handleIndexChange = useCallback(
    ({ realIndex }) => {
      onSelect && onSelect(images[realIndex]);
    },
    [images, onSelect],
  );

  const params = useMemo(
    () => ({
      mainCarouselParams: {
        initialSlide: activeIndex,
        onSlideChange: handleIndexChange,
      },
    }),
    [activeIndex, handleIndexChange],
  );

  const allClasses = useMemo(
    () => ({
      root: classes?.root,
      carousel: {
        slide: { "w-auto": false },
        ...(classes?.carousel || {}),
      },
    }),
    [classes],
  );

  return (
    <Modal classes={allClasses.root} {...modal}>
      <Carousel
        variant="fullscreen"
        classes={allClasses.carousel}
        params={params}
        onInstance={setPrimaryInstance}
      >
        {images.map((image) =>
          image.customImage ? (
            image.customImage
          ) : (
            <Image
              key={image.id}
              src={image.src}
              classes={{ root: "w-100 h-100 object-fit-contain" }}
            />
          ),
        )}
      </Carousel>
    </Modal>
  );
};

export default FullscreenImage;
